// Deploys & config: identity per Lambda in the list; its env keys and digests in the right panel.
import React from 'react';
import type { Deploys as DeploysData, EnvName, LambdaIdentity } from '../../../shared/types';
import { GPill, GStatus } from '../design/atoms';
import { DetailHeader, DetailSection, MasterDetail, selectedRow, useEscape } from '../design/MasterDetail';
import { Empty, SectionCard } from '../design/Section';
import { fmtAge, fmtDateTime } from '../fmt';
import { useEnvStore } from '../store/envStore';
import { useResource } from '../store/resourceStore';
import { useSelection } from '../store/uiStore';

const STAMP_ENDPOINT: Record<string, string> = { server: 'api', auth: 'token' };

function Detail({ l, other, otherName, stamp, onClose }: { l: LambdaIdentity; other?: LambdaIdentity; otherName: EnvName; stamp?: { buildSha: string | null; buildTime: string | null }; onClose: () => void }) {
  const onlyThere = other ? other.envKeys.filter((k) => !l.envKeys.includes(k)) : [];
  const fact = (k: string, v: React.ReactNode) => <div style={{ overflowWrap: 'anywhere' }}><span style={{ color: 'var(--fg-4)' }}>{k} </span>{v}</div>;
  return (
    <>
      <DetailHeader title={l.fn} eyebrow={`${l.label} · ${l.owner} · ${l.runtime} ${l.architectures.join('/')}`} onClose={onClose} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontFamily: 'var(--ff-code)', fontSize: 12.5 }}>
        {stamp?.buildSha && fact('commit', <>{stamp.buildSha} <span style={{ color: 'var(--fg-4)' }}>built {stamp.buildTime ? fmtAge(stamp.buildTime) : '?'}</span></>)}
        {fact('code hash', l.codeSha256)}
        {fact('deployed', `${fmtAge(l.lastModified)} · ${fmtDateTime(l.lastModified)}`)}
        {fact('memory / timeout', `${l.memoryMb} MB / ${l.timeoutS} s`)}
        {fact('reserved concurrency', l.reservedConcurrency ?? 'none')}
        {fact('state', `${l.state}${l.lastUpdateStatus ? ` · ${l.lastUpdateStatus}` : ''}`)}
      </div>
      <DetailSection label={`Env keys · ${l.envKeys.length} · digest = first 10 hex of sha256(value); values are never read here`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {l.envKeys.map((k) => {
            const o = other?.envDigest[k];
            const mark = !other ? null : o === undefined ? `only here` : o === l.envDigest[k] ? 'same value' : 'differs';
            return (
              <div key={k} style={{ display: 'flex', gap: 10, fontFamily: 'var(--ff-code)', fontSize: 12, minWidth: 0 }}>
                <span style={{ color: 'var(--fg-2)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{k}</span>
                <span style={{ color: 'var(--fg-4)', marginLeft: 'auto', flexShrink: 0 }}>{l.envDigest[k]}</span>
                {mark && <span style={{ flexShrink: 0, color: mark === 'differs' ? 'var(--s-review-fg)' : mark === 'only here' ? 'var(--s-work-fg)' : 'var(--fg-5)' }}>{mark}</span>}
              </div>
            );
          })}
        </div>
        {other && onlyThere.length > 0 && <div style={{ marginTop: 8, fontFamily: 'var(--ff-code)', fontSize: 12, color: 'var(--s-work-fg)' }}>only in {otherName}: {onlyThere.join(', ')}</div>}
        {other && onlyThere.length === 0 && l.envKeys.every((k) => k in other.envDigest) && <div style={{ marginTop: 8, fontFamily: 'var(--ff-code)', fontSize: 12, color: 'var(--fg-4)' }}>same key set in both environments</div>}
        {!other && <div style={{ marginTop: 8, fontFamily: 'var(--ff-code)', fontSize: 12, color: 'var(--fg-4)' }}>{otherName} is unavailable, so there is nothing to compare against.</div>}
      </DetailSection>
    </>
  );
}

export function Deploys() {
  const env = useEnvStore((s) => s.env);
  const other: EnvName = env === 'prod' ? 'test' : 'prod';
  const here = useResource<DeploysData>(`/api/env/${env}/deploys`);
  const there = useResource<DeploysData>(`/api/env/${other}/deploys`);
  const otherLambdas = there.data?.lambdas.ok ? there.data.lambdas.data : [];
  const d = here.data;
  const [open, setOpen] = useSelection('deploys');
  const close = React.useCallback(() => setOpen(null), [setOpen]);
  useEscape(close, !!open);
  const lambdas = d?.lambdas.ok ? d.lambdas.data : [];
  const current = lambdas.find((l) => l.id === open) ?? null;
  const stampFor = (l: LambdaIdentity) => { const ep = STAMP_ENDPOINT[l.id]; const probe = ep && d?.health.ok ? d.health.data.find((h) => h.id === ep) : undefined; return probe ? { buildSha: probe.buildSha, buildTime: probe.buildTime } : undefined; };

  const list = (
    <>
      <SectionCard title="Lambdas" hint={`commit from the server's own build stamp where it reports one, else the code hash · click a row for its env keys · compared with ${other}${there.error || (there.data && !there.data.lambdas.ok) ? ' (unavailable)' : ''}`} section={d?.lambdas} loading={here.loading} onRetry={here.reload}
        right={<GPill onClick={here.reload} disabled={here.loading}>Refresh</GPill>}>
        {(rows) => (
          <div style={{ overflowX: 'auto' }}>
            <table className="pd">
              <thead><tr><th>Function</th><th>Code</th><th>Deployed</th><th>Runtime</th><th className="num">Mem</th><th className="num">Timeout</th><th className="num">Reserved</th><th className="num">Env keys</th><th>State</th></tr></thead>
              <tbody>{rows.map((l) => { const stamp = stampFor(l); return (
                <tr key={l.id} className="row-hover" aria-selected={open === l.id || undefined} style={selectedRow(open === l.id)} onClick={() => setOpen(l.id)}>
                  <td><div style={{ fontFamily: 'var(--ff-code)' }}>{l.fn}</div><div style={{ color: 'var(--fg-4)', fontSize: 12 }}>{l.label} · {l.owner}</div></td>
                  <td title={stamp?.buildSha ? `commit ${stamp.buildSha}, built ${stamp.buildTime} · code hash ${l.codeSha256}` : l.codeSha256} style={{ fontFamily: 'var(--ff-code)', fontSize: 12 }}>
                    {stamp?.buildSha ? <>{stamp.buildSha.slice(0, 10)}<div style={{ color: 'var(--fg-4)', fontSize: 11 }}>commit · built {stamp.buildTime ? fmtAge(stamp.buildTime) : '?'}</div></> : <>{l.codeSha256.slice(0, 12)}<div style={{ color: 'var(--fg-4)', fontSize: 11 }}>code hash</div></>}
                  </td>
                  <td title={l.lastModified}>{fmtAge(l.lastModified)}<div style={{ color: 'var(--fg-4)', fontSize: 12, fontFamily: 'var(--ff-code)' }}>{fmtDateTime(l.lastModified)}</div></td>
                  <td style={{ fontFamily: 'var(--ff-code)', fontSize: 12, color: 'var(--fg-3)' }}>{l.runtime} · {l.architectures.join('/')}</td>
                  <td className="num">{l.memoryMb} MB</td>
                  <td className="num">{l.timeoutS} s</td>
                  <td className="num">{l.reservedConcurrency ?? '–'}</td>
                  <td className="num">{l.envKeys.length}</td>
                  <td><GStatus kind={l.state === 'Active' && (l.lastUpdateStatus === 'Successful' || !l.lastUpdateStatus) ? 'ok' : 'warn'} label={l.state.toLowerCase()} /></td>
                </tr>
              ); })}</tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Deploy and drift history" hint="recorded by the jobs Lambda every 15 minutes; a change in code hash is a deploy, anything else is drift" section={d?.history} loading={here.loading} onRetry={here.reload}>
        {(rows) => rows.length === 0 ? <Empty>Nothing recorded yet. The first run only takes a baseline; changes appear from the next deploy on.</Empty> : (
          <table className="pd">
            <thead><tr><th>When</th><th>What</th><th>Detail</th></tr></thead>
            <tbody>{rows.map((e) => (
              <tr key={e.id}><td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(e.at)}</td><td><GStatus kind={e.kind === 'deploy' ? 'work' : e.kind === 'reset' ? 'err' : 'warn'} label={e.kind} /> {e.title}</td><td style={{ fontFamily: 'var(--ff-code)', fontSize: 12, color: 'var(--fg-3)' }}>{e.detail}</td></tr>
            ))}</tbody>
          </table>
        )}
      </SectionCard>

      <SectionCard title="CloudFormation stacks" hint="the server stack imports its Lambdas by ARN; deploys mutate $LATEST outside of it" section={d?.stacks} loading={here.loading} onRetry={here.reload}>
        {(rows) => (
          <table className="pd">
            <thead><tr><th>Stack</th><th>Status</th><th>Last update</th><th>Description</th></tr></thead>
            <tbody>{rows.map((s) => (
              <tr key={s.name}><td style={{ fontFamily: 'var(--ff-code)' }}>{s.name}</td><td><GStatus kind={/COMPLETE$/.test(s.status) && !/ROLLBACK/.test(s.status) ? 'ok' : /unreadable/.test(s.status) ? 'dim' : 'warn'} label={s.status.toLowerCase().slice(0, 40)} /></td><td>{s.lastUpdated ? `${fmtAge(s.lastUpdated)} · ${fmtDateTime(s.lastUpdated)}` : '–'}</td><td style={{ color: 'var(--fg-3)' }}>{s.description}</td></tr>
            ))}</tbody>
          </table>
        )}
      </SectionCard>
    </>
  );
  return <MasterDetail list={list} detail={current ? <Detail key={current.id} l={current} other={otherLambdas.find((o) => o.id === current.id)} otherName={other} stamp={stampFor(current)} onClose={close} /> : null} />;
}
